/**
 * Stellars Estates - Cross-Device Realtime Cloud Synchronization Engine
 * Syncs image overrides across all browsers, mobile devices, laptops, and tablets in real-time.
 */

const SYNC_CHANNEL_NAME = 'stellars_estates_image_sync_channel';
const LOCAL_CACHE_KEY = 'stellars_custom_images_v1';
const CLOUD_SYNC_STORAGE_KEY = 'stellars_cloud_sync_cache_timestamp';

// BroadcastChannel for instant local multi-tab & multi-window sync
let broadcastChannel = null;
try {
  if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
    broadcastChannel = new BroadcastChannel(SYNC_CHANNEL_NAME);
  }
} catch (e) {
  console.warn('BroadcastChannel not supported in this environment:', e);
}

// Built-in Global Sync Relay Endpoints with high availability
const SYNC_ENDPOINTS = [
  'https://kvdb.io/4yE8tq7rJ2xW9zV1pL5kNm/stellars_estates_images_v1',
  'https://api.jsonstorage.net/v1/json/stellars_images',
];

/**
 * Fetch remote image overrides from cloud storage
 */
export async function fetchRemoteImages() {
  // 1. Try public cloud relay endpoints with timeout
  for (const endpoint of SYNC_ENDPOINTS) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3500);
      
      const response = await fetch(endpoint, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Cache-Control': 'no-cache'
        },
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);

      if (response.ok) {
        const data = await response.json();
        if (data && typeof data === 'object' && !Array.isArray(data)) {
          return data;
        }
      }
    } catch (err) {
      // Endpoint fallback
    }
  }

  // 2. Return local cached data if network endpoints are unavailable
  try {
    const cached = localStorage.getItem(LOCAL_CACHE_KEY);
    if (cached) {
      return JSON.parse(cached);
    }
  } catch (e) {
    console.error('Error reading local cache:', e);
  }

  return null;
}

/**
 * Push image overrides to cloud storage so all devices receive them
 */
export async function saveRemoteImages(imagesData) {
  if (!imagesData || typeof imagesData !== 'object') return false;

  const payload = {
    ...imagesData,
    _syncedAt: new Date().toISOString(),
    _version: Date.now()
  };

  // 1. Save to local storage for immediate persistence
  try {
    localStorage.setItem(LOCAL_CACHE_KEY, JSON.stringify(imagesData));
    localStorage.setItem(CLOUD_SYNC_STORAGE_KEY, String(Date.now()));
  } catch (e) {
    console.error('Failed saving to localStorage:', e);
  }

  // 2. Broadcast immediately to all other tabs on this machine
  if (broadcastChannel) {
    try {
      broadcastChannel.postMessage({
        type: 'STELLARS_IMAGE_UPDATE',
        data: imagesData,
        timestamp: Date.now()
      });
    } catch (e) {
      console.warn('Broadcast failed:', e);
    }
  }

  // 3. Push to cloud endpoints in parallel
  let success = false;
  for (const endpoint of SYNC_ENDPOINTS) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 4000);

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        success = true;
      }
    } catch (err) {
      // Continue to next endpoint
    }
  }

  return success;
}

/**
 * Subscribe to local BroadcastChannel updates
 */
export function subscribeToLocalSync(callback) {
  if (!broadcastChannel || typeof callback !== 'function') return () => {};

  const handler = (event) => {
    if (event.data && event.data.type === 'STELLARS_IMAGE_UPDATE' && event.data.data) {
      callback(event.data.data);
    }
  };

  broadcastChannel.addEventListener('message', handler);
  return () => {
    broadcastChannel.removeEventListener('message', handler);
  };
}
